import { css } from '@emotion/react';
import React, {
  useState,
  forwardRef,
  useEffect,
  useCallback,
} from 'react';
import { Button } from 'react-bootstrap';
import t from 'react-translate';
import {
  VideoPracticeActivity, VideoPracticeOption,
  VideoPracticeScenario, VideoPracticeType,
} from 'redux/schemas/models/video-practice';
import { MediaSourceType, requestMediaStream, stopMediaStreams } from 'recording/services/media-stream';

import {
  doubleSpacing, extraLargeSpacing, halfSpacing,
  standardSpacing, tripleSpacing,
} from 'styles/global_defaults/scaffolding';
import { black, gray2, gray3, hexToRgbaString, primary, shade } from 'styles/global_defaults/colors';
import { largeDesktop, handheld, screenXsMax } from 'styles/global_defaults/media-queries';

import NvIcon from 'shared/components/nv-icon';
import ClickableContainer from 'components/clickable-container';
import { useMediaQuery } from 'react-responsive';
import PracticeActivityProgress from './practice-activity-progress';
import VideoPracticeTitle from './video-practice-title';
import { RecordMode } from './practice-activity-modal';
import { getTimeLimits } from './video-practice-utils';
import CountdownTimer from './countdown-timer';

import { config } from '../../../../config/pendo.config.json';
import { config as appConfig } from '../../../../config/config.json';

export const CAMERA_SETTINGS_ARTICLE_LINK = `${appConfig.helpLink}/article/ART-1787`;

const CameraModeSelect = ({
  selectedMode,
  setSelectedMode,
}: {
  selectedMode: RecordMode;
  setSelectedMode: (mode: RecordMode) => void;
}) => {
  const [isCameraChecked, setIsCameraChecked] = useState(false);
  const [isCameraChecking, setIsCameraChecking] = useState(false);
  const [isCameraEnabled, setIsCameraEnabled] = useState(false);
  const [helpLinkClicked, setHelpLinkClicked] = useState(false);
  const ref = React.useRef(null);

  const getMediaStream = useCallback(() => {
    setIsCameraChecking(true);
    requestMediaStream(MediaSourceType.CAMERA_MIC)
      .then((stream: MediaStream) => {
        setIsCameraEnabled(true);
        stopMediaStreams(stream);
      })
      .catch((error) => {
        setIsCameraEnabled(false);
      })
      .finally(() => {
        setIsCameraChecked(true);
        if (isCameraChecked) {
          // Just to display the loader for a bit longer
          // to make it look like it's checking
          setTimeout(() => {
            setIsCameraChecking(false);
          }, 500);
        }
      });
  }, [isCameraChecked]);

  useEffect(() => {
    getMediaStream();
  }, [getMediaStream]);

  const checkAgain = (e) => {
    // A focus event is triggered when the user clicks on the link
    // and the focus state is not removed from the parent div.
    // So we need to remove the focus state from the parent div
    ref.current?.blur();
    getMediaStream();
  };

  return (
    <ClickableContainer
      ref={ref}
      className={`d-flex flex-column py-7 ${selectedMode === RecordMode.CAMERA ? 'active' : ''} ${!isCameraEnabled ? 'disabled' : ''}`}
      onClick={() => isCameraEnabled && setSelectedMode(RecordMode.CAMERA)}
      pendo-tag-name={config.pendo.practice.cameraMode}
      disabled={!isCameraChecked || !isCameraEnabled}
    >
      <NvIcon size='super-large' icon='camera-only' />
      <div>
        <div className='text-xl font-weight-bold mt-4 text-center'>
          {t.LECTURE_PAGES.COMPONENTS.VIDEO_PRACTICE.SCREEN_RECORDING.RECORDING_MODE.CAMERA()}
        </div>
        {isCameraChecked && !isCameraEnabled && (
          <div className='text-regular text-center'>
            {!helpLinkClicked && !isCameraChecking && (
              <React.Fragment>
                <div>
                  {t.LECTURE_PAGES.COMPONENTS.VIDEO_PRACTICE.SCREEN_RECORDING.RECORDING_MODE.CAMERA_DISABLED.NOT_AVAILABLE()}
                </div>
                <a
                  className='gray-3'
                  target='_blank'
                  rel='noopener noreferrer'
                  href={CAMERA_SETTINGS_ARTICLE_LINK}
                  onClick={() => setHelpLinkClicked(true)}
                >
                  {t.LECTURE_PAGES.COMPONENTS.VIDEO_PRACTICE.SCREEN_RECORDING.RECORDING_MODE.CAMERA_DISABLED.TROUBLESHOOT_WHY()}
                </a>
              </React.Fragment>
            )}
            {helpLinkClicked && !isCameraChecking && (
              <div className='check-again mx-3' onClick={checkAgain}>
                {t.LECTURE_PAGES.COMPONENTS.VIDEO_PRACTICE.SCREEN_RECORDING.RECORDING_MODE.CAMERA_DISABLED.CHECK_AGAIN()}
              </div>
            )}
            {isCameraChecking && (
              <div className='checking mx-3'>
                {t.LECTURE_PAGES.COMPONENTS.VIDEO_PRACTICE.SCREEN_RECORDING.RECORDING_MODE.CAMERA_DISABLED.CHECKING()}
              </div>
            )}
          </div>
        )}
      </div>
    </ClickableContainer>
  );
};

type SelectRecordingModeProps = {
  practiceType: VideoPracticeType;
  scenario: VideoPracticeScenario;
  activity?: VideoPracticeActivity;
  modalStepIndex: number;
  onNextStep: (mode: RecordMode) => void;
  take: number;
  onRetry: () => void
};

const styles = (practiceType) => css`
  background-color: ${hexToRgbaString(black, 0.95)};
  flex: 1;
  ${largeDesktop(css`
    min-height: 0;
  `)}

  .practice-activity-progress {
    margin-top: ${tripleSpacing}px;
    margin-bottom: ${extraLargeSpacing}px;
  }

  .details {
    width: 720px;

    .video-practice-title {
      margin-right: ${practiceType === VideoPracticeOption.ON_SPOT ? '100px' : 'initial'};
    }
  }

  .select-mode {
    max-width: 720px;
    min-height: 0;
    background-color: ${shade};
  }

  .option-container {
    flex-grow: 1;
    gap: ${doubleSpacing}px;
    color: ${gray3};

    > div {
      width: 50%;
      text-align: center;

      &:not(.disabled) {
        &:hover, &:focus, &.active {
          color: ${primary};
          outline: 2px solid ${primary};
        }
      }
      &.disabled {
        color: ${gray2};

        a {
          color: ${gray2};
          text-decoration: underline;
        }

        &:hover {
          color: ${gray3};

          a {
            color: ${gray3};
          }
        }
      }
    }
    .check-again {
      .clickable {
        cursor: pointer;
        text-decoration: underline;
      }
    }
    .checking{
      height: ${doubleSpacing}px;
    }
  }
  .button-container {
    display: flex;
    margin-top: ${tripleSpacing}px;
    margin-bottom: ${doubleSpacing}px;

    button:first-of-type {
      margin-right: ${halfSpacing}px;
    }
  }
  ${handheld(css`
    .practice-activity-progress {
      margin-bottom: 0;
    }

    .details {
      width: 100%;
      padding-right: ${standardSpacing}px;
      padding-left: ${standardSpacing}px;

      .video-practice-title {
        margin-right: ${standardSpacing}px;
      }
    }

    .button-container, .option-container {
      flex-direction: column;
      width: 100%;
      padding: ${standardSpacing}px;
    }

  `)};
`;

const SelectRecordingMode = forwardRef<any, SelectRecordingModeProps>(({
  practiceType,
  scenario,
  activity,
  modalStepIndex,
  onNextStep,
  take,
  onRetry,
}, ref) => {
  const isHandheld = useMediaQuery({
    query: `(max-width: ${screenXsMax}px)`,
  });

  const [selectedMode, setSelectedMode] = useState(null);

  const { timeToRecord } = getTimeLimits(scenario);

  const isRetryDisabled = practiceType === VideoPracticeOption.ON_SPOT && take >= activity.maxTries;

  return (
    <div
      css={styles(practiceType)}
      className='d-flex flex-column justify-content-center align-items-center'
    >
      <PracticeActivityProgress modalStepIndex={modalStepIndex} />
      <div className='course-title-small text-white text-center mb-5'>
        {t.LECTURE_PAGES.COMPONENTS.VIDEO_PRACTICE.SCREEN_RECORDING.RECORDING_MODE.SELECT_TITLE()}
      </div>
      <div className='details d-flex mb-1 justify-content-between'>
        <VideoPracticeTitle
          practiceType={practiceType}
          activityTitle={scenario.title}
          activity={activity}
          take={take}
        />
        <CountdownTimer countdownRemainingSeconds={timeToRecord} paused />
      </div>
      <div className='select-mode d-flex justify-content-center p-7 w-100'>
        <div className='d-flex option-container justify-content-between align-items-stretch mx-4 my-7'>
          <CameraModeSelect
            selectedMode={selectedMode}
            setSelectedMode={setSelectedMode}
          />
          <ClickableContainer
            className={`d-flex flex-column py-7 ${selectedMode === RecordMode.SCREEN ? 'active' : ''}`}
            onClick={() => setSelectedMode(RecordMode.SCREEN)}
            pendo-tag-name={config.pendo.practice.screenMode}
          >
            <NvIcon size='super-large' icon='screen' />
            <span className='text-xl font-weight-bold mt-4 text-center'>
              {t.LECTURE_PAGES.COMPONENTS.VIDEO_PRACTICE.SCREEN_RECORDING.RECORDING_MODE.SCREEN()}
            </span>
          </ClickableContainer>
        </div>
      </div>
      <div className='button-container'>
        <Button
          block={isHandheld}
          variant='secondary'
          onClick={onRetry}
          className='mr-4'
          disabled={isRetryDisabled}
        >
          {t.LECTURE_PAGES.COMPONENTS.VIDEO_PRACTICE.RETRY.TITLE()}
        </Button>
        <Button
          block={isHandheld}
          onClick={() => onNextStep(selectedMode)}
          disabled={!selectedMode}
        >
          {t.FORM.CONFIRM()}
        </Button>
      </div>
    </div>
  );
});

export default SelectRecordingMode;
